<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAdminMail } from '@/composables/useMail'
import { handleAxiosError } from '@/composables/useAxiosError'
import { formatBytes, formatNumber } from '@/utils'
import type { MailServerHealth, MailServerStats, MailQueueStatus } from '@/types/dashboard/mail'
import { DateTime } from 'luxon'

const { t } = useI18n()
const { getHealth, getStats, getQueue } = useAdminMail()

const loading = ref(true)
const health = ref<MailServerHealth | null>(null)
const stats = ref<MailServerStats | null>(null)
const queue = ref<MailQueueStatus | null>(null)

const healthStatus = computed(() => {
  if (!health.value) return 'unknown'
  if (health.value.stalwart_reachable && health.value.queue_healthy) return 'healthy'
  if (health.value.stalwart_reachable || health.value.queue_healthy) return 'degraded'
  return 'unhealthy'
})

const healthColor = computed(() => {
  switch (healthStatus.value) {
    case 'healthy':
      return 'success'
    case 'degraded':
      return 'warning'
    case 'unhealthy':
      return 'error'
    default:
      return 'grey'
  }
})


const formatDate = (dateStr: string): string => {
  return DateTime.fromISO(dateStr).toLocaleString(DateTime.DATETIME_MED)
}

const fetchData = async () => {
  loading.value = true
  try {
    const [healthRes, statsRes, queueRes] = await Promise.all([
      getHealth(),
      getStats(),
      getQueue()
    ])
    if (healthRes.data) health.value = healthRes.data
    if (statsRes.data) stats.value = statsRes.data
    if (queueRes.data) queue.value = queueRes.data
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'AdminMailServer' })
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchData()
})
</script>

<template>
  <div class="admin-mail-server">
    <div class="page-header">
      <h1>{{ t('dashboard.mail.admin.server.title') }}</h1>
      <button class="refresh-btn" :disabled="loading" @click="fetchData">
        <v-icon icon="mdi-refresh" size="18" :class="{ spinning: loading }" />
        <span>{{ t('common.refresh') }}</span>
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading && !health" class="loading-state">
      <v-progress-circular indeterminate size="48" width="3" color="primary" />
    </div>

    <template v-else>
      <!-- Health Status -->
      <div class="section-card">
        <div class="card-header">
          <h3>{{ t('dashboard.mail.admin.server.health.title') }}</h3>
          <v-chip :color="healthColor" size="small" variant="tonal">
            {{ t(`dashboard.mail.admin.overview.health.${healthStatus}`) }}
          </v-chip>
        </div>
        <div v-if="health" class="health-grid">
          <div class="health-item">
            <div class="health-icon" :class="health.stalwart_reachable ? 'success' : 'error'">
              <v-icon :icon="health.stalwart_reachable ? 'mdi-check' : 'mdi-close'" size="16" />
            </div>
            <div class="health-info">
              <span class="health-label">{{ t('dashboard.mail.admin.server.health.stalwart') }}</span>
              <span class="health-status">
                {{ health.stalwart_reachable ? t('dashboard.mail.admin.server.health.reachable') : t('dashboard.mail.admin.server.health.notReachable') }}
              </span>
            </div>
          </div>
          <div class="health-item">
            <div class="health-icon" :class="health.queue_healthy ? 'success' : 'error'">
              <v-icon :icon="health.queue_healthy ? 'mdi-check' : 'mdi-close'" size="16" />
            </div>
            <div class="health-info">
              <span class="health-label">{{ t('dashboard.mail.admin.server.health.queue') }}</span>
              <span class="health-status">
                {{ health.queue_healthy ? t('dashboard.mail.admin.server.health.healthy') : t('dashboard.mail.admin.server.health.unhealthy') }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Queue Status -->
      <div class="section-card">
        <div class="card-header">
          <h3>{{ t('dashboard.mail.admin.server.queue.title') }}</h3>
          <v-chip color="blue" size="small" variant="tonal">
            {{ queue?.pending ?? 0 }} {{ t('dashboard.mail.admin.overview.queue.pending') }}
          </v-chip>
        </div>
        <div v-if="queue?.messages && queue.messages.length > 0" class="table-wrapper">
          <table class="queue-table">
            <thead>
              <tr>
                <th>{{ t('dashboard.mail.admin.server.queue.table.from') }}</th>
                <th>{{ t('dashboard.mail.admin.server.queue.table.to') }}</th>
                <th>{{ t('dashboard.mail.admin.server.queue.table.retries') }}</th>
                <th>{{ t('dashboard.mail.admin.server.queue.table.nextRetry') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(msg, idx) in queue.messages" :key="idx">
                <td>
                  <code class="email-code">{{ msg.from }}</code>
                </td>
                <td>
                  <code class="email-code">{{ Array.isArray(msg.to) ? msg.to.join(', ') : msg.to }}</code>
                </td>
                <td>{{ msg.retry_count }}</td>
                <td>{{ msg.next_retry ? formatDate(msg.next_retry) : '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="empty-queue">
          <v-icon icon="mdi-check-circle" size="24" color="success" />
          <span>{{ t('dashboard.mail.admin.overview.queue.empty') }}</span>
        </div>
      </div>

      <!-- Statistics -->
      <div class="section-card">
        <div class="card-header">
          <h3>{{ t('dashboard.mail.admin.server.stats.title') }}</h3>
        </div>
        <div v-if="stats" class="stats-content">
          <!-- Total Stats -->
          <div class="stats-section">
            <h4>{{ t('dashboard.mail.admin.server.stats.total') }}</h4>
            <div class="stats-grid">
              <div class="stat-item">
                <v-icon icon="mdi-email-arrow-left" size="20" color="primary" />
                <div class="stat-info">
                  <span class="stat-value">{{ formatNumber(stats.messages_in_total) }}</span>
                  <span class="stat-label">{{ t('dashboard.mail.admin.overview.stats.messagesIn') }}</span>
                </div>
              </div>
              <div class="stat-item">
                <v-icon icon="mdi-email-arrow-right" size="20" color="primary" />
                <div class="stat-info">
                  <span class="stat-value">{{ formatNumber(stats.messages_out_total) }}</span>
                  <span class="stat-label">{{ t('dashboard.mail.admin.overview.stats.messagesOut') }}</span>
                </div>
              </div>
              <div class="stat-item">
                <v-icon icon="mdi-email-off" size="20" color="warning" />
                <div class="stat-info">
                  <span class="stat-value">{{ formatNumber(stats.spam_rejected_total) }}</span>
                  <span class="stat-label">{{ t('dashboard.mail.admin.overview.stats.spamRejected') }}</span>
                </div>
              </div>
              <div class="stat-item">
                <v-icon icon="mdi-shield-alert" size="20" color="error" />
                <div class="stat-info">
                  <span class="stat-value">{{ formatNumber(stats.auth_failures_total) }}</span>
                  <span class="stat-label">{{ t('dashboard.mail.admin.overview.stats.authFailures') }}</span>
                </div>
              </div>
              <div class="stat-item">
                <v-icon icon="mdi-download" size="20" color="blue" />
                <div class="stat-info">
                  <span class="stat-value">{{ formatBytes(stats.bytes_in_total) }}</span>
                  <span class="stat-label">{{ t('dashboard.mail.domainSection.stats.bytesIn') }}</span>
                </div>
              </div>
              <div class="stat-item">
                <v-icon icon="mdi-upload" size="20" color="green" />
                <div class="stat-info">
                  <span class="stat-value">{{ formatBytes(stats.bytes_out_total) }}</span>
                  <span class="stat-label">{{ t('dashboard.mail.domainSection.stats.bytesOut') }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Per-Domain Stats -->
          <div v-if="stats.domains && Object.keys(stats.domains).length > 0" class="stats-section">
            <h4>{{ t('dashboard.mail.admin.server.stats.perDomain') }}</h4>
            <div class="domains-table-wrapper">
              <table class="domains-table">
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>{{ t('dashboard.mail.domainSection.stats.messagesIn') }}</th>
                    <th>{{ t('dashboard.mail.domainSection.stats.messagesOut') }}</th>
                    <th>{{ t('dashboard.mail.domainSection.stats.spamRejected') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(domainStats, domain) in stats.domains" :key="domain">
                    <td>
                      <code class="domain-code">{{ domain }}</code>
                    </td>
                    <td>{{ formatNumber(domainStats.messages_in) }}</td>
                    <td>{{ formatNumber(domainStats.messages_out) }}</td>
                    <td>{{ formatNumber(domainStats.spam_rejected) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.admin-mail-server {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;

  h1 {
    font-size: 1.5rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
    margin: 0;
  }
}

.refresh-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: rgba(120, 163, 255, 0.1);
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.8);
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: rgba(120, 163, 255, 0.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: 80px;
}

.section-card {
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  h3 {
    font-size: 1rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
    margin: 0;
  }
}

.health-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.health-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(120, 163, 255, 0.05);
  border-radius: 8px;
}

.health-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;

  &.success {
    background: rgba(76, 175, 80, 0.2);
    color: #81c784;
  }

  &.error {
    background: rgba(244, 67, 54, 0.2);
    color: #ef5350;
  }
}

.health-info {
  display: flex;
  flex-direction: column;
}

.health-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.health-status {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.6);
}

.table-wrapper,
.domains-table-wrapper {
  overflow-x: auto;
}

.queue-table,
.domains-table {
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    text-align: left;
    padding: 10px 14px;
  }

  th {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgba(200, 218, 255, 0.5);
    background: rgba(15, 23, 48, 0.3);
  }

  td {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.85);
    border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  }

  tr:last-child td {
    border-bottom: none;
  }
}

.email-code,
.domain-code {
  padding: 2px 6px;
  background: rgba(120, 163, 255, 0.1);
  border-radius: 4px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.8rem;
}

.empty-queue {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px;
  color: rgba(200, 218, 255, 0.6);
  font-size: 0.9rem;
}

.stats-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.stats-section {
  h4 {
    font-size: 0.85rem;
    font-weight: 600;
    color: rgba(200, 218, 255, 0.7);
    margin: 0 0 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: rgba(120, 163, 255, 0.05);
  border-radius: 8px;
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

.stat-label {
  font-size: 0.75rem;
  color: rgba(200, 218, 255, 0.6);
}

/* Light Theme */
.v-theme--lightTheme {
  .page-header h1 {
    color: rgba(15, 23, 42, 0.95);
  }

  .section-card {
    background: rgba(255, 255, 255, 0.7);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .card-header h3 {
    color: rgba(15, 23, 42, 0.95);
  }

  .health-label {
    color: rgba(15, 23, 42, 0.9);
  }

  .queue-table th,
  .domains-table th {
    color: rgba(15, 23, 42, 0.6);
    background: rgba(15, 23, 42, 0.03);
  }

  .queue-table td,
  .domains-table td {
    color: rgba(15, 23, 42, 0.85);
  }

  .stat-value {
    color: rgba(15, 23, 42, 0.95);
  }
}
</style>
